import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { MapInteraction } from 'react-map-interaction';
import Ripples from 'react-ripples';
import { getD3Formatter } from '../languages'
import { group, deviation } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { geoAlbersUsa, geoPath } from 'd3-geo';
import { geoMiller } from 'd3-geo-projection';
import { sortBy } from 'lodash';

import Tooltip from './Tooltip'
import { perCapitaLabelMaker } from '../formatters'
import { getText } from '../languages';

import globe from '../images/ic_globe.svg'
import regionInfo from '../images/ic_regioninfo.svg'
import './style.scss'

import NoDataPattern from '../NoDataPattern'

const countyPaths = {};

function getCentroid(path, geometry) {
  const [x, y] = path.centroid(geometry);
  return { x, y };
};

function mapToNest(map) {
  return Array.from(map, ([key, values]) => ({key, values}));
}

function filterGeometry(geometry, filterByDeviation = false) {
  const target = Object.assign({}, geometry);
  if (target.geometry) {
    if (target.geometry.type === 'MultiPolygon') {
      const { coordinates } = target.geometry;
      const innerCoordinates = coordinates.map(c => c[0]).sort((a, b) => b.length - a.length);
      const coordinateFilter = filterByDeviation && innerCoordinates.length > 1 ? (
          ['036', '410'].includes(target.id) // special case for Australia / Tasmania, South Korea / Jeju
            ? (c, i) => i < 2
            : c => c.length >= Math.floor(deviation(innerCoordinates.map(c => c.length)))
        ) : (c, i) => (i < 1);
      const filteredCoordinates = innerCoordinates.filter(coordinateFilter);
      target.geometry = {
        type: 'Polygon',
        coordinates: filteredCoordinates,
      };
    }
  }
  return target;
}

const defaultTransform = { translation: { x: 0, y: 0 }, scale: 1 };
const scaleSettings = { min: 1, max: 10 };

const MapChart = (props) => {
  const svgRef = useRef();
  const {
    regionType,
    regionsById,
    populations,
    metricKey,
    mapKey,
    colorScale,
    language,
    isWorld,
    directionality,
    subnationalUnits,
    showSubnationalUnits,
    clickRegion,
    selectedRegionId,
    selectedDatum,
    subnationalFeatures,
    countries,
    countriesHighRes,
    states,
    counties,
    countiesHighRes,
  } = props;

  const formatNumber = useCallback((value) => getD3Formatter(language)(',.0f')(value), [language]);

  const [transform, setTransform] = useState(defaultTransform);
  const [transition, setTransition] = useState(false);
  const runMapTransition = useMemo(() => function() {
    setTransition(true);
    const mapTransitionHandle = setTimeout(() => setTransition(false), 350);
    return () => clearTimeout(mapTransitionHandle);
  }, [setTransition]);
  const isDefaultTransform = useMemo(() =>
    defaultTransform.translation.x === transform.translation.x &&
    defaultTransform.translation.y === transform.translation.y &&
    defaultTransform.scale === transform.scale
  , [transform]);
  const resetTransform = useMemo(() => (
    function() {
      if (!isDefaultTransform) {
        runMapTransition();
        setTransform(defaultTransform);
      }
    }
  ), [isDefaultTransform, runMapTransition]);

  const [hoveredCounty, setHoveredCounty] = useState(null)
  const [mapResetAt, setMapResetAt] = useState(Date.now())
  if (!countyPaths[mapKey]) {
    countyPaths[mapKey] = {}
  }

  const regionsToUse = useMemo(() => (
    isWorld
      ? props.zoom === null
        ? countries.filter(props.filter)
        : countriesHighRes.concat(showSubnationalUnits ? subnationalFeatures : []).filter(props.filter)
        // : countries.concat(showSubnationalUnits ? subnationalFeatures : []).filter(props.filter)
      : props.zoom === null
        ? counties.filter(props.filter)
        : countiesHighRes.filter(props.filter)
    ).filter(region => region.id !== undefined)
  , [isWorld, props.zoom, props.filter, showSubnationalUnits, counties, countiesHighRes, countries, countriesHighRes, subnationalFeatures]);

  const statesToUse = useMemo(() => (
    isWorld
      ? props.zoom === null
        ? countries.filter(props.filter)
        : countriesHighRes.concat(showSubnationalUnits ? subnationalFeatures : []).filter(props.filter)
        // : countries.concat(showSubnationalUnits ? subnationalFeatures : []).filter(props.filter)
      : states.filter(props.filter)
    ).filter(region => region.id !== undefined)
  , [isWorld, props.zoom, props.filter, showSubnationalUnits, countries, countriesHighRes, subnationalFeatures, states]);

  const mapSettings = useMemo(() => {
    const defaultWidth = 960;
    const defaultHeight = 500;
    const width = props.width;
    const height = props.height || defaultHeight / defaultWidth * width;

    const projection = isWorld
      ? geoMiller().scale(150).rotate([-11, 0])
      : geoAlbersUsa();
    const projectionScale = projection.scale();
    const scaleMultiplier = width / defaultWidth;
    const newScale = projectionScale * scaleMultiplier;

    const baseTranslation = isWorld
      ? [width / 2, height / 3 * 2]
      : [width / 2, height / 2];

    projection
      .translate(baseTranslation)
      .scale(newScale);

    return { width, height, projection };
  }, [isWorld, props.width, props.height]);
  const { width, height, projection } = mapSettings;

  const pathGen = useMemo(() => {
    if (props.zoom) {
      if (props.zoom.type === 'county' || props.zoom.type === 'parentCountry') {
        const padding = Math.max(width, height) * 0.1
        projection.fitExtent(
          [[padding, padding], [width - padding, height - padding]],
          isWorld
            ? filterGeometry(regionsToUse.find(d => d.id === props.zoom.id), true)
            : regionsToUse.find(d => d.id === props.zoom.id)
        );
      } else if (props.zoom.type === 'state') {
        const padding = Math.max(width, height) * 0.05
        projection.fitExtent([[padding, padding], [width - padding, height - padding]], states.find(d => d.id === props.zoom.id ))
      }
    }
    return geoPath().projection(projection);
  }, [props.zoom, width, height, isWorld, projection, regionsToUse, states]);

  const lastProjectionSettings = useRef({});
  useEffect(() => {
    if (
      (
        lastProjectionSettings.current[mapKey]
        && (
          lastProjectionSettings.current[mapKey].width !== width
          || lastProjectionSettings.current[mapKey].height !== height
          || (
            props.zoom
            && lastProjectionSettings.current[mapKey].zoom
            && props.zoom.id !== lastProjectionSettings.current[mapKey].zoom.id
          )
        )
      )
      || !lastProjectionSettings.current[mapKey]
    ) {
      resetTransform();
      countyPaths[mapKey] = {}
      setMapResetAt(Date.now())
    }
    lastProjectionSettings.current[mapKey] = {
      width,
      height,
      zoom: props.zoom
    }
  }, [width, height, props.zoom, mapKey, mapResetAt, resetTransform])

  const populationKey = useMemo(() => (isWorld ? 'population' : 'POPESTIMATE2019'), [isWorld]);
  const { regionsWithData, regionProperties } = useMemo(() => {
    const regionProperties = {};
    const regionsWithData = regionsToUse
    .filter(region => region.id !== undefined)
    .map(region => {
      if (
        // (props.zoom && (props.zoom.type === 'county' || props.zoom.type === 'parentCountry'))
        (props.zoom && props.zoom.type === 'county')
          ||
        (regionsById[region.id] && regionsById[region.id].containsSubnational)
      ) {
        const targetGeometry = isWorld ? filterGeometry(region).geometry : region.geometry;
        if (isWorld) {
          const bounds = pathGen.bounds(targetGeometry);
          region.boundSize = Math.hypot(bounds[0][0] - bounds[1][0], bounds[0][1] - bounds[1][1]);
        }
        region.center = region.coordinates
          ? getCentroid(pathGen, region) // NYC
          : getCentroid(
              pathGen,
              targetGeometry
            );
      }
      const id = region.id;
      const regionData = regionsById[id];
      const population = id in populations ? populations[id][populationKey] : null;
      if (!regionProperties[id]) {
        regionProperties[id] = {
          perCapita: null,
          value: null,
          population: population,
          containsSubnational: false,
        };
      }
      if (regionData && population) {
        const totalValue = regionData.data[regionData.data.length - 1][metricKey] || null;
        const lastValue = regionData.data[regionData.data.length - 1][`new_${metricKey}_14dayAverage`];
        regionProperties[id].perCapita = lastValue / population;
        regionProperties[id].value = lastValue;
        regionProperties[id].totalValue = totalValue;
        regionProperties[id].population = population;
        regionProperties[id].containsSubnational = regionData.containsSubnational ? true : false;
        if (isNaN(regionProperties[id].perCapita)) {
          console.warn('isNaN(regionProperties[id].perCapita)');
          regionProperties[id].perCapita = null;
        }
        if (isNaN(regionProperties[id].totalValue)) {
          regionProperties[id].totalValue = null;
        }
      }
      return region;
    });
    return { regionsWithData, regionProperties };
  }, [isWorld, metricKey, pathGen, populationKey, populations, props.zoom, regionsById, regionsToUse]);

  const hasTouch = 'ontouchstart' in window
  const disableTooltips = hasTouch

  const hoverCounty = (county) => {
    return (event) => {
      if (disableTooltips) {
        return null
      }
      if (!county) {
        setHoveredCounty(null)
        return
      }
      setHoveredCounty({ county, x: event.clientX, y: event.clientY })
    }
  }

  const usMapRef = useRef();
  let tooltip = null
  if (hoveredCounty) {
    tooltip = <Tooltip hoveredCounty={hoveredCounty} properties={regionProperties[hoveredCounty.county.id]} usMapRef={usMapRef} populationKey={populationKey} {...props} />
  }

  const summary = useMemo(() => {
    if (regionType === 'usa' || regionType === 'world') {
      const colors = colorScale.range();
      const summaryData = mapToNest(group(
          regionsWithData,
          region => regionProperties[region.id].perCapita === 0 ? '#fff' : colorScale(regionProperties[region.id].perCapita),
        )).map(group => {
          group.index = colors.indexOf(group.key);
          group.category = colorScale.invertExtent(group.key);
          const decimalPlaces = 0;
          if (group.index === -1) {
            group.label = 0;
          } else if (group.index === 0) {
            group.label = metricKey === 'cases' ? '' : '> 0';
          } else if (group.index === colors.length - 1) {
            group.label = `> ${perCapitaLabelMaker(group.category[0], decimalPlaces, language)}`;
          } else {
            group.label = perCapitaLabelMaker(group.category[0], decimalPlaces, language);
          }
          return group;
        }).sort((a, b) => a.index - b.index);

      const summaryWidth = 815;
      const outerWidth = width * 0.75;
      const scale = outerWidth / summaryWidth;
      const summaryHeight = 60;
      const groupHeight = 15;
      const minSegmentWidth = groupHeight * 1.5;
      const sectionHeight = 20;
      const totalGroupSize = summaryData.map(group => group.values.length).reduce((a, v) => a + v, 0);
      const summaryXScale = scaleLinear().domain([0, totalGroupSize]).range([minSegmentWidth, summaryWidth]);
      const falseTotalWidth = summaryData.map(group => summaryXScale(group.values.length)).reduce((a, v) => a + v, 0);
      const getGroupWidth = group => (summaryXScale(group.values.length) / falseTotalWidth) * summaryWidth;
      let xOffset = 0;

      return (
        <div className='summary'>
          <svg width={width} height={summaryHeight * scale}>
            <defs>
              <NoDataPattern />
            </defs>
            <g transform={`translate(${(width - outerWidth) / 2}, 5)`}>
              <g transform={`scale(${scale})`}>
                <g transform={`translate(${minSegmentWidth}, 0)`}>
                  <rect width={summaryWidth} height={summaryHeight} fill='white' />
                  <text fontSize={11} dy={14} textAnchor={directionality === 'rtl' ? 'end' : 'start'}>
                    {`${getText(language, metricKey === 'cases' ? 'casesOfCovid19Per100kPeople' : 'fatalitiesOfCovid19Per100kPeople')} ${getText(language, 'inLast14Days')}`}
                  </text>
                  {scale > 0.7 ? (
                    <text fontSize={11} dy={14} x={summaryWidth} textAnchor={directionality === 'rtl' ? 'start' : 'end'}>
                      {getText(language, isWorld ? 'barSizesWorld' : 'barSizesUS')}
                    </text>
                  ) : null }
                  <g key='N/A' transform={`translate(${-minSegmentWidth * 2}, ${sectionHeight})`}>
                    <rect
                      width={minSegmentWidth}
                      height={groupHeight}
                      stroke='#E2E0E0'
                      fill='url(#noDataPattern)'
                    />
                    <text
                      transform={`translate(${minSegmentWidth / 2}, ${sectionHeight})`}
                      fill='black'
                      textAnchor='middle'
                      fontSize={10}
                      dy={10}
                    >
                      {getText(language, 'noData')}
                    </text>
                  </g>
                  {
                    summaryData.map(group => {
                      const groupWidth = getGroupWidth(group);
                      const thisOffset = xOffset;
                      xOffset += groupWidth;
                      return (
                        <g key={group.key} transform={`translate(${thisOffset}, ${sectionHeight})`}>
                          <rect
                            width={groupWidth}
                            height={groupHeight}
                            fill={group.key}
                            stroke={group.index === -1 ? '#E2E0E0' : group.key}
                          />
                          {
                            group.index === -1
                              ? null
                              : <line y2={sectionHeight} stroke='#E2E0E0' />
                          }
                          <text
                            transform={`translate(${0}, ${sectionHeight})`}
                            fill='black'
                            textAnchor='middle'
                            fontSize={10}
                            dy={10}
                          >
                            {group.label}
                          </text>
                        </g>
                      );
                    })
                  }
                </g>
              </g>
            </g>
          </svg>
        </div>
      );
    }
    return null;
  }, [colorScale, isWorld, language, metricKey, regionType, regionsWithData, regionProperties, width, directionality]);

  const subnationalIndicators = useMemo(() => (
    regionsWithData
      .filter(region => (
        (region.center && regionsById[region.id])
          &&
        (region.id !== selectedRegionId && (!selectedDatum || selectedDatum.parent !== region.id))
          ? regionsById[region.id].containsSubnational
          : false
      ))
    ), [regionsWithData, regionsById, selectedRegionId, selectedDatum]);

  const subnationalInstructionsIcons = useMemo(() => (
    subnationalIndicators.map(region => {
      const size = 16;
      const invertedScale = 1 / transform.scale;
      const offset = invertedScale * (size / 2);
      return (
        <g
          key={region.id}
          transform={`translate(${region.center.x - offset}, ${region.center.y - offset}) scale(${invertedScale})`}
        >
          <image
            width={size}
            height={size}
            href={regionInfo}
            alt='(i)'
          />
        </g>
      );
    })
  ), [subnationalIndicators, transform.scale]);

  // const subnationalInstructionsLabels = useMemo(() => (
  //   subnationalIndicators.map(region => {
  //     const size = 16;
  //     const overHalfWidth = region.center.x > width / 2;
  //     const objectX = overHalfWidth ? width * -1 : 0;
  //     const invertedScale = 1 / transform.scale;
  //     const offset = invertedScale * (size / 2);
  //     return (
  //       <g
  //         key={region.id}
  //         style={{ pointerEvents: 'none' }}
  //         transform={`translate(${region.center.x - offset}, ${region.center.y - offset}) scale(${invertedScale})`}
  //       >
  //         <image
  //           width={size}
  //           height={size}
  //           href={regionInfo}
  //           alt='(i)'
  //           style={{ pointerEvents: 'auto', cursor: 'pointer', opacity: 0 }}
  //           onClick={clickRegion(region, metricKey)}
  //           onTouchStart={clickRegion(region, metricKey)}
  //         />
  //         <foreignObject
  //           style={{ pointerEvents: 'none' }}
  //           transform={`translate(${objectX}, ${-size / 3})`}
  //           width='100%'
  //           height='100%'
  //         >
  //           <div style={{ position: 'fixed', width: '100%'}}>
  //             <div
  //               className='subnationalInstructions'
  //               style={{
  //                 right: overHalfWidth ? size * 0.5 : null,
  //                 left: overHalfWidth ? null : size * 1.5,
  //               }}
  //             >
  //               {getText(language, 'subnationalInstructions')}
  //             </div>
  //           </div>
  //         </foreignObject>
  //       </g>
  //     );
  //   })
  // ), [clickRegion, language, metricKey, subnationalIndicators, transform.scale, width]);

  const regionLabels = useMemo(() => {
    let maxLabels = 12;
    const isCountyOrCountry = regionType === 'county' || regionType === 'country';
    // const approxRegionCenters = {};
    const labelPositions = [];
    if (isCountyOrCountry) {
      if (showSubnationalUnits) {
        maxLabels = Math.max(subnationalUnits.length, maxLabels);
      }
      return (
        sortBy(
          sortBy(
            showSubnationalUnits
              ? regionsWithData.filter(region => {
                  let include = false;
                  if (regionsById[region.id]) {
                    // if (regionsById[region.id].parent === props.zoom.id) {
                    //   include = true;
                    // }
                    if (regionsById[props.zoom.id] && regionsById[props.zoom.id].parent) {
                      if (regionsById[props.zoom.id].parent === regionsById[region.id].parent) {
                        include = true;
                      }
                    }
                  }
                  return include;
                })
              : regionsWithData
            , region => -region.boundSize
          )
          , region => (props.zoom && (props.zoom.type === 'county' || props.zoom.type === 'parentCountry')) && (props.zoom.id === region.id) ? -1 : 0
        )
        .filter(region => {
          const inView = region.center.x >= 0 && region.center.x <= width && region.center.y >= 0 && region.center.y <= height;
          const hasData = regionsWithData.length > maxLabels ? (regionProperties[region.id].value !== null) : true;
          return inView && hasData;
        })
        .slice(0, Math.min(Math.floor(maxLabels), regionsWithData.length))
        .map(region => {
          const countLabel = regionProperties[region.id].value !== null ? formatNumber(regionProperties[region.id].value) : getText(language, 'noData');
          const bubbleHeight = 16;
          const bubbleWidth = Math.max(countLabel.length * 10, 32);

          // nudge labels away from the center of the label it's overlapping
          const { x, y } = region.center;
          let tx = x;
          let ty = y;

          const inRect = p => (tx >= p.x0 && tx <= p.x1) && (ty >= p.y0 && ty <= p.y1)
          // const inRect = p => ((
          //     (tx - bubbleWidth / 2 >= p.x0 && tx - bubbleWidth / 2 <= p.x1) && (ty - bubbleHeight / 2 >= p.y0 && ty - bubbleHeight / 2 <= p.y1)
          //   ) || (
          //     (tx + bubbleWidth / 2 >= p.x0 && tx + bubbleWidth / 2 <= p.x1) && (ty - bubbleHeight / 2 >= p.y0 && ty - bubbleHeight / 2 <= p.y1)
          //   ) || (
          //     (tx - bubbleWidth / 2 >= p.x0 && tx - bubbleWidth / 2 <= p.x1) && (ty + bubbleHeight / 2 >= p.y0 && ty + bubbleHeight / 2 <= p.y1)
          //   ) || (
          //     (tx + bubbleWidth / 2 >= p.x0 && tx + bubbleWidth / 2 <= p.x1) && (ty + bubbleHeight / 2 >= p.y0 && ty + bubbleHeight / 2 <= p.y1)
          //   )
          // );

          let overlap = labelPositions.find(inRect);
          while (overlap !== undefined) {
            overlap = labelPositions.find(inRect);
            if (overlap) {
              tx += overlap.dx;
              ty += overlap.dy;
            }
          }

          const quadrantBounds = [
            {
              x0: tx - bubbleWidth * 0.75, y0: ty - bubbleHeight * 0.75,
              x1: tx, y1: ty,
              dx: -bubbleWidth * 0.75, dy: -bubbleHeight * 0.75,
            },
            {
              x0: tx, y0: ty - bubbleHeight * 0.75,
              x1: tx + bubbleWidth * 0.75, y1: ty,
              dx: bubbleWidth * 0.75, dy: -bubbleHeight * 0.75,
            },
            {
              x0: tx - bubbleWidth * 0.75, y0: ty,
              x1: tx, y1: ty + bubbleHeight * 0.75,
              dx: -bubbleWidth * 0.75, dy: bubbleHeight * 0.75,
            },
            {
              x0: tx, y0: ty,
              x1: tx + bubbleWidth * 0.75, y1: ty + bubbleHeight * 0.75,
              dx: bubbleWidth * 0.75, dy: bubbleHeight * 0.75,
            },
          ];
          labelPositions.push(...quadrantBounds);
          // nudge labels away from the center of the label it's overlapping

          return (
            <g
              key={region.id}
              // transform={`translate(${region.center.x}, ${region.center.y})`}
              transform={`translate(${tx}, ${ty})`}
              style={{ pointerEvents: 'none' }}
            >
              {
                region.id === selectedRegionId ? (
                  <rect transform={`translate(${-bubbleWidth / 2}, 0)`} width={bubbleWidth} height={16} rx={8} fill='black' />
                ) : null
              }
              <text fontSize={14} dy={13} textAnchor='middle' fill={region.id === selectedRegionId ? 'white' : 'black'}>
                {countLabel}
              </text>
            </g>
          );
        })
      );
    }
    return null;
  }, [regionType, regionsWithData, regionProperties, props.zoom, selectedRegionId, language, width, height, formatNumber, regionsById, showSubnationalUnits, subnationalUnits.length]);

  const alaksaHawaiiLines = useMemo(() => {
    if (props.zoom === null && regionType === 'usa') {
      // these values were determined by using https://bl.ocks.org/Fil/7723167596af40d9159be34ffbf8d36b and making a few slight changes to those numbers
      const alaskaTopY = (height / 2) + ( height / 2) * (0.115 / 0.234)
      const alaskaEndX = (width / 2) - (width / 2) * (0.245 / 0.425);
      const hawaiiTopY = (height / 2) + ( height / 2) * (0.160 / 0.234)
      const hawaiiLeftX = (width / 2) - (width / 2) * (0.203 / 0.425);
      const hawaiiMidX = (width / 2) - (width / 2) * (0.145 / 0.425);
      const hawaiiMidY = (height / 2) + ( height / 2) * (0.215 / 0.234)
      const hawaiiEndX = (width / 2) - (width / 2) * (0.10 / 0.425);
      const hawaiiEndY = (height / 2) + ( height / 2) * (0.23 / 0.234)
      const hawaiiLeftEndX = (width / 2) - (width / 2) * (0.19 / 0.425);
      return (
        <g stroke='#CCCCCC'>
          <line y1={alaskaTopY} y2={alaskaTopY} x1={0} x2={alaskaEndX} />
          <line y1={alaskaTopY} y2={hawaiiTopY} x2={hawaiiLeftX} x1={alaskaEndX} />
          <line y1={hawaiiTopY} y2={hawaiiTopY} x2={hawaiiLeftX} x1={hawaiiMidX} />
          <line y1={hawaiiTopY} y2={hawaiiMidY} x2={hawaiiEndX} x1={hawaiiMidX} />
          <line y1={hawaiiMidY} y2={hawaiiTopY} x2={hawaiiLeftX} x1={hawaiiLeftX} />
          <line y1={hawaiiMidY} y2={hawaiiEndY} x2={hawaiiEndX} x1={hawaiiEndX} />
          <line y1={hawaiiMidY} y2={hawaiiEndY} x1={hawaiiLeftX} x2={hawaiiLeftEndX} />
        </g>
      );
    }
    return null;
  }, [props.zoom, regionType, width, height]);

  const controls = useMemo(() => {

    function zoomButton(delta) {
      const scale = Math.max(scaleSettings.min, Math.min(scaleSettings.max, transform.scale * delta));

      const { x, y } = transform.translation;

      const scaleOffset0 = {
        x: (((width * transform.scale) - width)) / 2,
        y: (((height * transform.scale) - height)) / 2,
      };

      const translation0 = {
        x: (x + scaleOffset0.x) / transform.scale,
        y: (y + scaleOffset0.y) / transform.scale,
      };

      const scaleOffset = {
        x: (width - (width * scale)) / 2,
        y: (height - (height * scale)) / 2,
      };

      const translation = {
        x: translation0.x * scale + scaleOffset.x,
        y: translation0.y * scale + scaleOffset.y,
      };

      runMapTransition();
      setTransform({ scale, translation });
    };

    return isWorld && !selectedRegionId ? (
      <div className="controls">
        <div className='rippleButton'>
          <Ripples>
            <button
              type="button"
              disabled={isDefaultTransform}
              className={`reset ${isDefaultTransform ? 'inactive' : ''}`}
              style={{ width: '30px', paddingTop: '5px'}}
              onClick={resetTransform}
            >
              <img src={globe} alt='Reset' />
            </button>
          </Ripples>
        </div>
        <div className='rippleButton'>
          <Ripples>
            <button
              type="button"
              disabled={transform.scale >= scaleSettings.max}
              className={`${transform.scale >= scaleSettings.max ? 'inactive' : ''}`}
              style={{ width: '30px', paddingTop: '5px' }}
              onClick={() => zoomButton(1.25)}
            >
              +
            </button>
          </Ripples>
        </div>
        <div className='rippleButton'>
          <Ripples>
            <button
              type="button"
              disabled={transform.scale <= scaleSettings.min}
              className={`${transform.scale <= scaleSettings.min ? 'inactive' : ''}`}
              onClick={() => zoomButton(0.75)}
            >
              -
            </button>
          </Ripples>
        </div>
      </div>
    ) : null;
  }, [isWorld, isDefaultTransform, resetTransform, transform, width, height, runMapTransition, selectedRegionId]);

  let selectedCountyStrokePath = null;
  let hoveredCountyStrokePath = null;

  return (
    <div key={mapKey} className='usMap' ref={usMapRef}>
      {controls}
      <div style={{ width, height, pointerEvents: !(isWorld && !selectedRegionId) ? 'none' : 'auto' }}>
        <MapInteraction
          showControls={false}
          disableZoom={true}
          disablePan={!(isWorld && !selectedRegionId)}
          scale={transform.scale}
          minScale={scaleSettings.min}
          maxScale={scaleSettings.max}
          translation={transform.translation}
          onChange={isWorld ? setTransform : null}
        >
        {
          ({ translation, scale }) => (
            <svg ref={svgRef} width={width} height={height}>
              <defs>
                <NoDataPattern />
              </defs>
              <g
                key='animated'
                className={`transformContainer ${transition ? 'transition' : ''}`}
                style={{ transform: `translate(${translation.x}px, ${translation.y}px) scale(${scale})`, pointerEvents: 'auto' }}
              >
                {alaksaHawaiiLines}
                <g className='regions'>
                  {
                    regionsWithData.map(region => {
                      const fill = regionProperties[region.id].perCapita === null
                        ? 'url(#noDataPattern)'
                        : regionProperties[region.id].perCapita === 0
                          ? '#fff'
                          : colorScale(regionProperties[region.id].perCapita);

                      if (!(region.id in countyPaths[mapKey])) {
                        countyPaths[mapKey][region.id] = pathGen(region);
                      }
                      let fillOpacity = 1;
                      if (showSubnationalUnits) {
                        if (subnationalUnits.find(u => u.id === region.id) || fill === 'url(#noDataPattern)') {
                          fillOpacity = 1;
                        } else {
                          if (selectedRegionId === region.id || selectedDatum.parent === region.id) {
                            return null;
                          }
                          fillOpacity = 0;
                        }
                      }
                      const path = countyPaths[mapKey][region.id];
                      const strokeWidth = isWorld ? 1.5 : 3;
                      if (props.zoom && (props.zoom.type === 'county' || props.zoom.type === 'parentCountry' || props.zoom.type === 'state')) {
                        if (props.zoom.id === region.id || props.zoom.highlightCountyId === region.id) {
                          selectedCountyStrokePath = <path d={path} fill='none' stroke='#2e2e2e' strokeWidth={strokeWidth / scale} pointerEvents='none' />
                        }
                      }
                      if (hoveredCounty && hoveredCounty.county.id === region.id) {
                        fillOpacity = 1;
                        hoveredCountyStrokePath = <path d={path} fill='none' stroke='#2e2e2e' strokeWidth={strokeWidth / scale} pointerEvents='none' />
                      }
                      return (
                        <path
                          onClick={clickRegion(region, metricKey)}
                          onTouchStart={clickRegion(region, metricKey)}
                          key={region.id}
                          d={path}
                          fill={fill}
                          fillOpacity={fillOpacity}
                          id={`fips-${region.id}`}
                          onMouseOver={hoverCounty(region)}
                          onMouseOut={hoverCounty(null)}
                          onMouseMove={hoverCounty(region)}
                        />
                      )
                    })
                  }
                </g>
                <g className='outlines'>
                  {
                    statesToUse.map(state => {
                      if (!(state.id in countyPaths[mapKey])) {
                        countyPaths[mapKey][state.id] = pathGen(state)
                      }
                      if (showSubnationalUnits && (selectedRegionId === state.id || selectedDatum.parent === state.id)) {
                        return null;
                      }
                      const fill = 'none'
                      const stroke = '#2e2e2e'
                      const path = countyPaths[mapKey][state.id]
                      const strokeWidth = isWorld ? 0.5 : 1
                      return (
                        <path key={state.id} d={path} stroke={stroke} strokeWidth={strokeWidth / transform.scale} fill={fill} id={`fips-${state.id}`}/>
                      )
                    })
                  }
                  {selectedCountyStrokePath}
                  {hoveredCountyStrokePath}
                </g>
                <g className='subnationalIndicators'>
                  <g key='icons' id='icons' style={{ pointerEvents: 'none' }}>
                    {subnationalInstructionsIcons}
                  </g>
                  {
                    // <g key='labels' id='labels' style={{ pointerEvents: 'none' }}>
                    //   {subnationalInstructionsLabels}
                    // </g>
                  }
                </g>
                <g className='regionLabels'>
                  {regionLabels}
                </g>
              </g>
            </svg>
          )
        }
        </MapInteraction>
      </div>
      {summary}
      {tooltip}
    </div>
  );
};

MapChart.defaultProps = {
  filter: d => true,
  zoom: null,
}

export default MapChart;
